import {
  Order,
  OrderStatusReason,
  OrderStatusReasonClearingExpiredOrder,
  OrderStatusReasonOrderCancelled,
  OrderStatusReasonOrderChanged,
  OrderStatusReasonUnspecified,
} from '@/api/spec';
import { generateBlockLogId } from '@/utils/generateBlockLogId.ts';
import { OrderHistory } from '@/feature/order_entry/ViewOrder.ts';
import { orderStatusMapper, OrderStatus } from '@/feature/order_entry/order_types.ts';
import type { MarketData } from '@/common/MarketData';
import { isTradeOrderType } from 'ui-kit';
import { getOrderErrorInfo } from '../../trading/utils/orderErrorHandling';
import { getOrderDisplayType } from './getOrderDisplayType';
import { deriveOrderFields, scaleValue } from './scaleOrderFields';

/** Statuses that leave the trader asking "why?". Filled/Open rows only restate the cell. */
const explainedStatuses: ReadonlySet<OrderStatus> = new Set([OrderStatus.failed, OrderStatus.canceled, OrderStatus.expired]);

/**
 * Reasons whose copy adds nothing to the status word — or misleads: "an error occurred" on a plain
 * cancellation (Unspecified), "updated successfully" on the Canceled row an edit leaves (OrderChanged).
 */
const unexplanatoryReasons: ReadonlySet<OrderStatusReason> = new Set([
  OrderStatusReasonUnspecified,
  OrderStatusReasonOrderCancelled,
  OrderStatusReasonClearingExpiredOrder,
  OrderStatusReasonOrderChanged,
]);

/** Resolved in the producer so ui-kit stays free of order-reason copy and both feeds agree. */
function getStatusTooltip(order: Order, orderStatus: OrderStatus): string | undefined {
  if (!explainedStatuses.has(orderStatus) || unexplanatoryReasons.has(order.sr)) {
    return undefined;
  }

  return getOrderErrorInfo(order.sr, undefined, order.fr).message;
}

export function mapOrderHistoryToViewModel(orderHistory: Order[], markets: ReadonlyMap<number, MarketData>): OrderHistory[] {
  return orderHistory
    .map((order: Order): OrderHistory | undefined => {
      // Skip non-trade operations (Cancel, IncreasePositionCollateral, unspecified): they have
      // no direction/size and would render as phantom "Open Short / 0 / Market" rows — a
      // forwarder-de-duplicated copy would even show as a spurious "Failed" trade. These
      // operations surface in Account History instead.
      if (!isTradeOrderType(order.t)) {
        return undefined;
      }

      const market = markets.get(order.mkt);
      if (!market) {
        return undefined;
      }

      const { side, isClose, isBuy, assetDecimals, quoteDecimals, assetCurrency } = deriveOrderFields(order.t, market);

      const scaledPrice = scaleValue(order.p, quoteDecimals);
      const scaledFillPrice = scaleValue(order.fp, quoteDecimals);
      const triggerPrice = scaleValue(order.tp, quoteDecimals);
      const scaledSize = scaleValue(order.os, assetDecimals, 0);
      const filledSize = scaleValue(order.fs, assetDecimals);

      let orderValue: number | undefined;
      if (filledSize && scaledFillPrice) {
        orderValue = scaledFillPrice * filledSize;
      } else if (scaledPrice) {
        orderValue = scaledPrice * scaledSize;
      }

      const orderType = getOrderDisplayType(order.t, scaledPrice, triggerPrice);

      // liquidation order is created by smart contract, and has 0 leverage
      const orderStatus = !order.rq && order.lv === 0 ? OrderStatus.liquidation : orderStatusMapper[order.st];

      return {
        dateTimestamp: order.at.t,
        txHash: order.at?.txid,
        assetCurrency,
        assetDecimals,
        quoteDecimals,
        marketId: order.mkt,
        side,
        size: scaledSize,
        isClose,
        isBuy,
        price: scaledPrice,
        fillingPrice: scaledFillPrice,
        orderValue,
        reduceOnly: isClose,
        triggerPrice,
        takeProfit: undefined,
        stopLoss: undefined,
        type: orderType,
        filledSize,
        orderStatus,
        statusReason: order.sr,
        failureReason: order.fr,
        statusTooltip: getStatusTooltip(order, orderStatus),
        orderId: order.oid.toString(),
        uniqueId: generateBlockLogId(order.at).toString(),
      };
    })
    .filter((v) => v != null);
}
